import React from 'react'
import styled from '@emotion/styled'
import { Divider, Form, Input, Spin, Steps } from 'antd'
import {
  CheckOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { Button, Text } from '@organice/atoms'
import { Users } from '@organice/graphql'
import { useUserMutations } from '@organice/utils/hooks'

import { ApolloError } from '@apollo/client'
// At least one numeric, one lowercase letter, one uppercase letter and one special char
const PASSWORD_RULES =
  /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{10,}$/

type ValidateStatus = Parameters<typeof Form.Item>[0]['validateStatus']

export interface ChangePasswordValidationProps {
  value: string
  validateStatus?: ValidateStatus
  errorMsg?: string | null
}

export interface ChangePasswordProps {
  user: Users
  onChange?(passwordSet: boolean): void
  onCancel?(): void
  onError?(status: ApolloError): void
  noFormWrapper?: boolean
}

export const ChangePassword: React.FC<ChangePasswordProps> = ({
  user,
  onChange,
  onCancel,
  onError,
  noFormWrapper = false,
}) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [currentStep, setCurrentStep] = React.useState(0)

  const { updateUserPassword, updatePasswordLoading } = useUserMutations({
    onUpdatedPassword: success => onChange && onChange(success),
  })

  const handleNextStep = async () => {
    try {
      await form.validateFields(['password', 'passwordRepeat'])
      setCurrentStep(1)
    } catch {
      // Validation failed, stay on current step
    }
  }

  const handlePreviousStep = () => {
    setCurrentStep(0)
  }

  const saveForm = () => {
    updateUserPassword({
      variables: {
        ownPassword: form.getFieldValue('ownPassword'),
        password: form.getFieldValue('password'),
        id: user.id,
      },
      update(cache, { data }) {
        const setPassword = data?.setPassword
        if (!setPassword) return

        form.resetFields()
        setCurrentStep(0)
        const userToUpdate = {
          __typename: 'users',
          id: user.id,
        }
        cache.modify({
          id: cache.identify(userToUpdate),
          fields: {
            hasPassword(cachedUser) {
              return true
            },
          },
          broadcast: true,
        })
      },
      onError() {
        form.setFields([
          {
            name: 'ownPassword',
            errors: [t('user.errors.updatePassword')],
          },
        ])
      },
    })
  }

  const step1Form = (
    <>
      <Form.Item
        label={t('user.password')}
        name="password"
        rules={[
          {
            required: true,
          },
          () => ({
            validator(_, value) {
              return value && value.match(PASSWORD_RULES)
                ? Promise.resolve()
                : Promise.reject(new Error(t('user.passwordValidation')))
            },
          }),
        ]}
        hasFeedback
      >
        <Input.Password autoComplete="new-password" spellCheck="false" />
      </Form.Item>
      <Form.Item
        label={t('user.passwordRepeat')}
        name="passwordRepeat"
        hasFeedback
        rules={[
          {
            required: true,
          },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (value && getFieldValue('password') === value) {
                return Promise.resolve()
              }
              return Promise.reject(
                new Error(t('user.passwordRepeatValidation'))
              )
            },
          }),
        ]}
      >
        <Input.Password autoComplete="new-password" spellCheck="false" />
      </Form.Item>
    </>
  )

  const step2Form = (
    <OwnPasswordContainer>
      <Text>{t('user.ownPasswordDescription')}</Text>
      <Form.Item
        label={t('user.ownPassword')}
        name="ownPassword"
        rules={[
          {
            required: true,
          },
          () => ({
            validator(_, value) {
              return value
                ? Promise.resolve()
                : Promise.reject(new Error(t('user.ownPasswordValidation')))
            },
          }),
        ]}
      >
        <Input.Password autoComplete="current-password" spellCheck="false" />
      </Form.Item>
    </OwnPasswordContainer>
  )

  const pswdForm = (
    <>
      <StepsContainer>
        <Steps
          current={currentStep}
          size="small"
          items={[
            { title: t('user.newPassword') },
            { title: t('user.confirmIdentity') },
          ]}
        />
      </StepsContainer>
      <Divider />
      <StepContent style={{ display: currentStep === 0 ? 'block' : 'none' }}>
        {step1Form}
      </StepContent>
      <StepContent style={{ display: currentStep === 1 ? 'block' : 'none' }}>
        {step2Form}
      </StepContent>
    </>
  )

  if (noFormWrapper) {
    return pswdForm
  }

  return (
    <Form form={form} name="basic" onFinish={saveForm} labelCol={{ span: 10 }}>
      <Spin spinning={updatePasswordLoading}>{pswdForm}</Spin>
      <Divider />
      <ButtonContainer>
        <Button onClick={() => onCancel && onCancel()}>
          {t('user.cancel')}
        </Button>
        {currentStep === 0 ? (
          <Button
            icon={<ArrowRightOutlined />}
            type="primary"
            onClick={handleNextStep}
          >
            {t('user.next')}
          </Button>
        ) : (
          <>
            <Button icon={<ArrowLeftOutlined />} onClick={handlePreviousStep}>
              {t('user.back')}
            </Button>
            <Button
              icon={<CheckOutlined />}
              type="primary"
              onClick={() => form.submit()}
              loading={updatePasswordLoading}
            >
              {t('user.setPassword')}
            </Button>
          </>
        )}
      </ButtonContainer>
    </Form>
  )
}
const ButtonContainer = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',

  '& > button': {
    '&:not(:last-of-type)': {
      marginRight: '0.625rem',
    },
  },
})

const OwnPasswordContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  marginTop: '1rem',
})

const StepsContainer = styled.div({
  marginBottom: '1rem',
})

const StepContent = styled.div({
  minHeight: '150px',
})
